/**
 * tenantScope — one place that knows "which tenant is this tab signed in as"
 * and which module-level caches must be dropped when that changes.
 *
 * IDI-304: useCompanyProfile / useBranding / settingsListCache were
 * module-level and unkeyed, and logout() cleared none of them. A logout ->
 * login in the same tab served the PREVIOUS dealer's company name, logo and
 * brand colours (onto customer-facing quote/invoice documents) and their
 * settings lists (crews, job types ...) until a hard reload. IDI-44 fixed the
 * same bug for localStorage.tenantId and stopped one layer short.
 *
 * Two independent guards (ruled 2026-09-10: ship BOTH):
 *   1. resetTenantScopedCaches() — called from logout() AND whenever the
 *      signed-in tenant changes (setCurrentTenant with a different id).
 *   2. Every cache records the tenant it was filled for and refuses to serve
 *      an entry whose tenant !== getCurrentTenant(). So a future logout path
 *      that forgets (1) still cannot leak.
 *
 * Any NEW module-level cache of tenant data must registerTenantReset() here.
 */

let currentTenant = null;
const resetters = new Set();

export function getCurrentTenant() {
  return currentTenant;
}

/** Register a cache's reset fn. Returns an unregister fn. */
export function registerTenantReset(fn) {
  resetters.add(fn);
  return () => resetters.delete(fn);
}

export function resetTenantScopedCaches() {
  resetters.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      // One broken cache must not stop the others clearing.
      // eslint-disable-next-line no-console
      console.error('[tenantScope] cache reset threw:', err);
    }
  });
}

/**
 * Record the signed-in tenant (AuthContext on login / checkAuth; null on
 * logout). A CHANGE of tenant resets every registered cache.
 */
export function setCurrentTenant(tenantId) {
  const next = tenantId || null;
  if (next === currentTenant) return;
  currentTenant = next;
  resetTenantScopedCaches();
}

/**
 * IDI-63 (F3): a super-admin opening a dealer's workspace scopes every cache
 * to that dealer (the request header follows the URL). Returns restore(),
 * which puts the signed-in key back (a change, so the caches reset again).
 */
export function enterDealerScope(dealerId) {
  const prev = currentTenant;
  setCurrentTenant(`dealer:${dealerId}::${prev || ''}`);
  return () => setCurrentTenant(prev);
}
